import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { S, FONT_HEAD, FONT_BODY } from './styles'

// Route + admin gate live in App.js (mirrors DealDeskReviewAdmin): render is
// guarded by `&& isAdmin`, and a bounce effect kicks non-admins to /library.
//
// Two writes only. An admin can delete a response that has no code issued,
// and can send the second CE code through the recruit-second-code edge
// function, which re-verifies the admin server-side. On the delete path:
// The DELETE policy on recruit_survey_responses enforces both halves —
// admin role and code_issued IS NULL — so a row that already has a code
// cannot be removed from here even if the button were reachable.
// Everything else on this screen is read-only; recruit-survey-submit owns
// every other write with a service-role client.
//
// RLS does the access control. Both recruit tables had RLS on with NO policies
// at all, which denies everything and returns an empty result rather than an
// error — so this screen could not exist until the two admin SELECT policies
// were added. They use the same profiles.role = 'admin' EXISTS subquery every
// other admin policy in this repo uses.

// The answers arrive as a JSON object keyed by question id, which is what the
// survey posts and what the FUB note renders. Raw keys are fine in a CRM
// timeline and wrong on a screen somebody reads every week, so they are mapped
// explicitly — an algorithm turning tools_now into "Tools Now" reads like a
// database, and a key added later still shows something rather than vanishing.
const ANSWER_FIELDS = [
  { key: 'tools_now',    label: 'Using today' },
  { key: 'which_tools',  label: 'Would use' },
  { key: 'time_sink',    label: 'Biggest time sink' },
  { key: 'missing',      label: 'Missing or unclear' },
  { key: 'would_change', label: 'Would it change work' },
  { key: 'opt_in',       label: 'Hear more' },
]

// Verified against a real record. NOT app.followupboss.com — this brokerage is
// on its own subdomain, and the generic host does not resolve to the person.
const FUB_PERSON_URL = 'https://homeprosarizona.followupboss.com/2/people/view/'

// Below this many unclaimed codes the number turns amber. The pool is refilled
// by hand, so the point of the colour is to ask for that before it empties
// mid-demo — a claim against an empty pool still saves the response but sends
// no code.
const CODES_LOW = 10

const CHIPS = {
  matched:  { background: '#E4EDF7', color: '#123763' },
  notfound: { background: '#FAEEDA', color: '#633806' },
  interest: { background: '#EAF3DE', color: '#27500A' },
}

const chipBase = {
  display: 'inline-flex', alignItems: 'center',
  padding: '3px 9px', borderRadius: '12px',
  fontSize: '11px', fontWeight: 500, fontFamily: FONT_BODY,
  whiteSpace: 'nowrap',
}

function Chip({ tone, children }) {
  return <span style={{ ...chipBase, ...(CHIPS[tone] || CHIPS.matched) }}>{children}</span>
}

const eyebrow = {
  fontSize: '10px', fontWeight: 500, letterSpacing: '1.5px',
  color: '#999', fontFamily: FONT_HEAD, marginBottom: '6px',
}

const mono = {
  fontFamily: "'Space Mono','SFMono-Regular',Menlo,monospace",
  fontSize: '11.5px',
}

// Arizona does not observe DST, so a bare toLocaleString on a machine set to
// Pacific reads an hour off for half the year. The zone is named rather than
// left to the browser: everyone reading this screen is in Phoenix, and the
// person who filled the survey in was too.
const PHOENIX = 'America/Phoenix'

function formatWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: PHOENIX }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: PHOENIX })
}

// One value out of the answers blob, rendered. Arrays are joined rather than
// stringified — ["a","b"] on screen is noise — and an empty answer shows a dash
// because question 4 is genuinely skippable and a blank row reads as a bug.
function answerText(answers, key) {
  const v = answers ? answers[key] : null
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—'
  const s = v == null ? '' : String(v).trim()
  return s || '—'
}

const tab = (active, disabled) => ({
  border: `0.5px solid ${active ? '#147efb' : '#e8e8e8'}`,
  borderRadius: '18px', padding: '5px 12px', fontSize: '12px',
  fontFamily: FONT_BODY, fontWeight: 500, whiteSpace: 'nowrap',
  background: active ? '#f4f4f2' : 'transparent',
  color: disabled ? '#bbb' : active ? '#1d2e42' : '#666',
  cursor: disabled ? 'default' : 'pointer',
})

const metricCard = {
  flex: '1 1 160px', minWidth: 0,
  background: 'white', border: '0.5px solid #e8e8e8', borderRadius: '12px',
  padding: '14px 16px', fontFamily: FONT_BODY,
}

function Metric({ label, children, note }) {
  return (
    <div style={metricCard}>
      <div style={eyebrow}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 700, color: '#1d2e42', fontFamily: FONT_HEAD, letterSpacing: '0.5px' }}>
        {children}
      </div>
      {note && <div style={{ fontSize: '11.5px', color: '#999', marginTop: '2px' }}>{note}</div>}
    </div>
  )
}

function Chevron({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  )
}

export default function RecruitingAdmin({ session, profile, showToast, navigateTo }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [codes, setCodes] = useState({ unclaimed: 0, total: 0 })
  const [expanded, setExpanded] = useState({})   // response id -> bool
  const [confirmDelete, setConfirmDelete] = useState(null)   // response id or null
  const [confirmSecond, setConfirmSecond] = useState(null)   // response id or null
  const [sendingSecond, setSendingSecond] = useState(null)   // response id or null

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Both tables at once — neither depends on the other, and the screen has
      // nothing to show until both are in.
      const [respRes, codesRes] = await Promise.all([
        supabase
          .from('recruit_survey_responses')
          .select('id, name, email, phone, answers, opted_in, fub_person_id, fub_matched, code_issued, second_code_issued, created_at')
          .order('created_at', { ascending: false }),
        // claimed_at is the whole story: null means unclaimed. Only the one
        // column is selected — the codes themselves are not needed to count
        // them, and an unclaimed code is a live credential.
        supabase
          .from('recruit_promo_codes')
          .select('claimed_at'),
      ])

      if (respRes.error) {
        console.error('[recruiting] responses load', respRes.error)
        showToast('Could not load responses: ' + respRes.error.message)
        setRows([])
      } else {
        setRows(respRes.data || [])
      }

      if (codesRes.error) {
        console.error('[recruiting] codes load', codesRes.error)
        setCodes({ unclaimed: 0, total: 0 })
      } else {
        const all = codesRes.data || []
        setCodes({ unclaimed: all.filter(c => c.claimed_at == null).length, total: all.length })
      }
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Mount only, matching DealDeskReviewAdmin: depending on `load` would re-fire
  // whenever App.js's showToast identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  const interested = rows.filter(r => r.opted_in === true).length
  const codesLow = codes.unclaimed < CODES_LOW

  // Same shape as deleteCase in App.js: .select() makes an RLS refusal
  // visible as zero rows instead of a silent no-op, and confirmDelete is
  // cleared on every exit so a row can't stay armed.
  async function deleteRow(id) {
    const { data: deleted, error } = await supabase
      .from('recruit_survey_responses').delete().eq('id', id).select()
    if (error) { showToast('Delete failed: ' + error.message); setConfirmDelete(null); return }
    if (!deleted || deleted.length === 0) {
      showToast('Delete was refused — the response is still there.')
      setConfirmDelete(null)
      return
    }
    setRows(prev => prev.filter(r => r.id !== id))
    setConfirmDelete(null)
    showToast('Response deleted')
  }

  // Second CE code, earned by taking the broker call. The edge function
  // re-checks that the caller is an admin, claims from the same pool,
  // stamps the row, and emails it. Returns the code because the caller
  // is a verified admin who can already read the row.
  async function sendSecondCode(id) {
    setSendingSecond(id)
    try {
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/recruit-second-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ response_id: id }),
      })
      const data = await res.json().catch(() => ({}))
      if (data.code) {
        setRows(prev => prev.map(r => r.id === id ? { ...r, second_code_issued: data.code } : r))
      }
      if (!res.ok) { showToast(data.error || 'Could not send the second code'); return }
      showToast(data.sent ? 'Second code sent' : 'Code claimed but the email failed. It is on the row.')
    } catch (err) {
      showToast('Could not send the second code: ' + err.message)
    } finally {
      setSendingSecond(null)
      setConfirmSecond(null)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '8px 0 18px' }}>
        <div style={{ fontSize: '14px', color: '#666', fontFamily: FONT_BODY, flex: '1 1 260px', minWidth: 0 }}>
          Demo feedback from recruiting prospects.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={tab(true, false)}>Feedback · {rows.length}</span>
          {/* Placeholder, deliberately inert: no onClick, and a span rather than
              a disabled button so nothing is focusable that does nothing. */}
          <span style={tab(false, true)}>Join leads</span>
          <button
            onClick={load}
            disabled={loading}
            style={{ ...tab(false, false), opacity: loading ? 0.5 : 1, cursor: loading ? 'default' : 'pointer' }}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <Metric label="CODES LEFT" note={`of ${codes.total} issued`}>
          <span style={{ color: codesLow ? '#633806' : '#1d2e42' }}>{codes.unclaimed}</span>
        </Metric>
        <Metric label="RESPONSES">{rows.length}</Metric>
        <Metric label="WANT TO HEAR MORE">
          <span style={{ color: '#27500A' }}>{interested}</span>
        </Metric>
      </div>

      {loading ? (
        <div style={{ padding: '40px 0', color: '#666', fontFamily: FONT_BODY }}>Loading responses…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '40px 0', color: '#999', fontSize: '14px', fontFamily: FONT_BODY }}>
          No responses yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {rows.map(r => (
            <ResponseRow
              key={r.id}
              row={r}
              expanded={!!expanded[r.id]}
              onToggle={() => setExpanded(e => ({ ...e, [r.id]: !e[r.id] }))}
              deletable={!r.code_issued}
              confirming={confirmDelete === r.id}
              onAskDelete={() => setConfirmDelete(r.id)}
              onConfirmDelete={() => deleteRow(r.id)}
              onCancelDelete={() => setConfirmDelete(null)}
              confirmingSecond={confirmSecond === r.id}
              sendingSecond={sendingSecond === r.id}
              onAskSecond={() => setConfirmSecond(r.id)}
              onConfirmSecond={() => sendSecondCode(r.id)}
              onCancelSecond={() => setConfirmSecond(null)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ResponseRow({ row, expanded, onToggle, deletable, confirming, onAskDelete, onConfirmDelete, onCancelDelete, confirmingSecond, sendingSecond, onAskSecond, onConfirmSecond, onCancelSecond }) {
  return (
    <div style={{ background: 'white', border: '0.5px solid #e8e8e8', borderRadius: '12px', fontFamily: FONT_BODY }}>
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
          padding: '13px 16px', background: 'transparent', border: 'none',
          textAlign: 'left', cursor: 'pointer', fontFamily: FONT_BODY, minWidth: 0,
        }}>
        {/* AGENT — flex:1 so it absorbs the slack and everything after it sits
            hard right; ellipsis so a long address clips rather than pushing the
            chevron out of the row. */}
        <span style={{ flex: '1 1 auto', minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
            <span style={{ fontSize: '13.5px', fontWeight: 500, color: '#1d2e42', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.name || 'No name given'}
            </span>
            {row.opted_in === true && <Chip tone="interest">Interested</Chip>}
          </span>
          <span style={{ ...mono, display: 'block', color: '#999', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.email}
          </span>
        </span>

        <span style={{ flexShrink: 0, fontSize: '11.5px', color: '#999', whiteSpace: 'nowrap' }}>
          {formatWhen(row.created_at)}
        </span>

        <span style={{ flexShrink: 0 }}>
          {row.fub_matched ? <Chip tone="matched">Matched</Chip> : <Chip tone="notfound">Not found</Chip>}
        </span>

        <span style={{ ...mono, flexShrink: 0, color: row.code_issued ? '#1d2e42' : '#ccc', whiteSpace: 'nowrap' }}>
          {row.code_issued || '—'}
        </span>

        <Chevron open={expanded} />
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ height: '0.5px', background: '#e8e8e8', margin: '0 0 14px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {ANSWER_FIELDS.map(f => (
              <div key={f.key}>
                <div style={eyebrow}>{f.label.toUpperCase()}</div>
                <div style={{ fontSize: '14px', lineHeight: 1.55, color: '#1d2e42', overflowWrap: 'anywhere' }}>
                  {answerText(row.answers, f.key)}
                </div>
              </div>
            ))}
          </div>

          {row.fub_person_id && (
            <a
              href={`${FUB_PERSON_URL}${row.fub_person_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '16px', color: '#147efb', fontSize: '12px', fontFamily: FONT_BODY, textDecoration: 'none' }}>
              Open in Follow Up Boss
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 17 17 7" /><polyline points="9 7 17 7 17 15" />
              </svg>
            </a>
          )}

          {row.code_issued && (
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ ...eyebrow, margin: 0 }}>SECOND CODE</span>
              {row.second_code_issued ? (
                <span style={{ ...mono, color: '#1d2e42' }}>{row.second_code_issued}</span>
              ) : confirmingSecond ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: '#1d2e42', fontFamily: FONT_BODY }}>Send to {row.email}?</span>
                  <button onClick={onConfirmSecond} disabled={sendingSecond} style={{ background: '#fdbf00', color: '#412402', border: 'none', borderRadius: '999px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, fontFamily: FONT_BODY, cursor: sendingSecond ? 'default' : 'pointer', opacity: sendingSecond ? 0.6 : 1 }}>{sendingSecond ? 'Sending…' : 'Yes'}</button>
                  <button onClick={onCancelSecond} disabled={sendingSecond} style={{ background: 'none', border: '1px solid #e8e8e8', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: '#555', fontFamily: FONT_HEAD }}>No</button>
                </div>
              ) : (
                <button onClick={onAskSecond} style={{ background: 'none', border: '1px solid #147efb', color: '#147efb', borderRadius: '999px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, fontFamily: FONT_BODY, cursor: 'pointer' }}>Send second code</button>
              )}
            </div>
          )}

          {deletable && (
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              {confirming ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#C8102E', fontFamily: FONT_BODY }}>Delete?</span>
                  <button onClick={onConfirmDelete} style={{ ...S.btnDanger, padding: '4px 10px', fontSize: '11px', flex: 'none' }}>Yes</button>
                  <button onClick={onCancelDelete} style={{ background: 'none', border: '1px solid #e8e8e8', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: '#555', fontFamily: FONT_HEAD }}>No</button>
                </div>
              ) : (
                <button onClick={onAskDelete} style={{ background: 'none', border: 'none', padding: 0, fontSize: '12px', color: '#999', cursor: 'pointer', fontFamily: FONT_BODY }}>Delete response</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
